import { cn } from '@/lib/utils';
import {
  Home,
  Folder,
  RotateCw,
  Phone,
  FileText,
  Activity,
  AlertTriangle,
  Send,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { IconBadge } from '@/modules/shared/components/ui/IconBadge';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

export type Patient360NavKey = 'overview' | 'activity' | 'cases' | 'cycles' | 'call-logs' | 'documents' | 'adverse-events' | 'communications';

export interface Patient360NavItem {
  key: Patient360NavKey;
  label: string;
  icon: LucideIcon;
  /** Optional: only shown when the permission is granted */
  menuKey?: string;
}

export const PATIENT_360_NAV_ITEMS: Patient360NavItem[] = [
  { key: 'overview',       label: 'Overview',       icon: Home },
  { key: 'cases',          label: 'Enrollment Cases', icon: Folder,       menuKey: 'assistance_cases' },
  { key: 'cycles',         label: 'Treatment Cycles', icon: RotateCw },
  { key: 'documents',      label: 'Documents',      icon: FileText },
  { key: 'communications', label: 'Communications', icon: Send },
  { key: 'call-logs',      label: 'Call Logs',      icon: Phone,          menuKey: 'call_logs' },
  { key: 'adverse-events', label: 'AE / PC',        icon: AlertTriangle },
  { key: 'activity',       label: 'Activity',       icon: Activity },
];

// ─────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────

interface PatientSidebarNavProps {
  activeKey: Patient360NavKey;
  onChange: (key: Patient360NavKey) => void;
  /** Subset of items visible to the current user. Defaults to all items. */
  visibleKeys?: Patient360NavKey[];
  /** Badge counts to display next to tab labels (e.g., { cases: 3, documents: 5 }) */
  badgeCounts?: Partial<Record<Patient360NavKey, number>>;
  /** Render only the desktop (vertical) variant. */
  desktopOnly?: boolean;
  /** Render only the mobile (horizontal) variant. */
  mobileOnly?: boolean;
  className?: string;
}

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

/**
 * Navigation for Patient360.
 *
 * Default: horizontal tab bar (used below smart action section).
 * desktopOnly: vertical left-rail (legacy, still available).
 * mobileOnly: horizontal scrollable bar (legacy, still available).
 */
export function PatientSidebarNav({
  activeKey,
  onChange,
  visibleKeys,
  badgeCounts,
  desktopOnly = false,
  mobileOnly = false,
  className,
}: PatientSidebarNavProps) {
  const items = visibleKeys
    ? PATIENT_360_NAV_ITEMS.filter((i) => visibleKeys.includes(i.key))
    : PATIENT_360_NAV_ITEMS;

  // ── Default: horizontal tab bar (all viewports) ─────────
  if (!desktopOnly && !mobileOnly) {
    return (
      <nav
        aria-label="Patient Details sections"
        className={cn(
          'flex overflow-x-auto gap-0 sticky top-0 z-10',
          className,
        )}
      >
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = item.key === activeKey;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => onChange(item.key)}
              aria-current={isActive ? 'page' : undefined}
              title={item.key === 'cases' ? "Represents the patient's enrollment in a support program." : item.key === 'cycles' ? 'Represents an individual treatment or support event within an enrollment.' : undefined}
              className={cn(
                'flex items-center rounded-lg px-3 py-2 text-xs font-medium transition-colors whitespace-nowrap shrink-0 gap-1.5',
                isActive
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              <IconBadge icon={Icon} variant={isActive ? 'blue' : 'muted'} size="compact" label={item.label} />
              {item.label}
              {badgeCounts?.[item.key] != null && badgeCounts[item.key]! > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[11px] font-medium bg-muted text-muted-foreground">
                  {badgeCounts[item.key]}
                </span>
              )}
            </button>
          );
        })}
      </nav>
    );
  }

  return (
    <>
      {/* ── Desktop: vertical left rail ──────────────────────── */}
      {!mobileOnly && (
          <nav
          aria-label="Patient Details sections"
          className={cn(
            'p360-left-nav',
            'py-3 px-2',
            className,
          )}
        >
          <div className="flex flex-col gap-0.5">
            {items.map((item) => {
              const Icon = item.icon;
              const isActive = item.key === activeKey;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => onChange(item.key)}
                  aria-current={isActive ? 'page' : undefined}
                  title={item.key === 'cases' ? "Represents the patient's enrollment in a support program." : item.key === 'cycles' ? 'Represents an individual treatment or support event within an enrollment.' : undefined}
                  className={cn(
                    'flex items-center rounded-lg px-3 py-2 text-xs font-medium transition-colors w-full gap-1.5',
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  <IconBadge icon={Icon} variant={isActive ? 'blue' : 'muted'} size="compact" label={item.label} />
                  {item.label}
                </button>
              );
            })}
          </div>
        </nav>
      )}

      {/* ── Mobile: horizontal scrollable bar ────────────────── */}
      {!desktopOnly && (
        <nav
          aria-label="Patient Details sections"
          className={cn(
            'flex lg:hidden overflow-x-auto gap-0 border-b border-border-hairline bg-surface-premium-muted',
            className,
          )}
        >
          {items.map((item) => {
            const Icon = item.icon;
            const isActive = item.key === activeKey;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => onChange(item.key)}
                aria-current={isActive ? 'page' : undefined}
                title={item.key === 'cases' ? "Represents the patient's enrollment in a support program." : item.key === 'cycles' ? 'Represents an individual treatment or support event within an enrollment.' : undefined}
                className={cn(
                  'flex items-center rounded-lg px-3 py-2 text-xs font-medium transition-colors whitespace-nowrap shrink-0 gap-1.5',
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                <IconBadge icon={Icon} variant={isActive ? 'blue' : 'muted'} size="compact" label={item.label} />
                {item.label}
              </button>
            );
          })}
        </nav>
      )}
    </>
  );
}
